import {useState, useCallback, useRef} from 'react';
import {useUrls} from '@/hooks/useConfig';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {isModuleEnabled} from '@/utils/modules';
import i18n from 'i18n';
import {buildURL} from '@/utils/urlBuilder';

interface ExplainChartResponse {
    success: boolean;
    explanation?: string;
    error?: string;
    chart_id?: number;
    metadata?: {
        tokens_used: number;
        latency_ms: number;
        has_data: boolean;
        timestamp?: string;
    };
}

interface UseChartExplainerReturn {
    isExplaining: boolean;
    explanation: string | null;
    error: string | null;
    streamingMessage: string;
    explainChart: (chartId: number) => Promise<void>;
    clearExplanation: () => void;
    cancelExplanation: () => void;
}

export const useChartExplainer = (): UseChartExplainerReturn => {
    const [isExplaining, setIsExplaining] = useState(false);
    const [explanation, setExplanation] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [streamingMessage, setStreamingMessage] = useState<string>('');
    const {urls, isLoaded: urlsLoaded} = useUrls();
    const {userData} = useOptimizedConfig();
    const abortControllerRef = useRef<AbortController | null>(null);
    const streamingContentRef = useRef<string>('');
    const isExplainingRef = useRef(false);

    const explainChart = useCallback(async (chartId: number) => {
        if (!chartId || isExplainingRef.current) {return;}

        if (!urlsLoaded) {
            setError('URLs not loaded yet');
            return;
        }

        const moduleEnabled = isModuleEnabled(userData, 'addon-explain-chart');
        if (!moduleEnabled || !urls?.explainChartStream) {
            setError(i18n.t('jt_explain_chart_not_enabled_tooltip.text') || 'Chart explanation module is not enabled');
            isExplainingRef.current = false;
            setIsExplaining(false);
            return;
        }

        isExplainingRef.current = true;
        setIsExplaining(true);
        setError(null);
        setExplanation(null);
        setStreamingMessage(i18n.t('jt_thinking.text'));
        streamingContentRef.current = '';

        abortControllerRef.current = new AbortController();

        try {
            const streamingUrl = buildURL('explainChartStream', {chart_id: chartId});
            const response = await fetch(streamingUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'text/event-stream',
                    'Cache-Control': 'no-cache',
                },
                credentials: 'same-origin',
                signal: abortControllerRef.current.signal
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error(i18n.t('jt_error_response_body_not_readable.text'));
            }

            const decoder = new TextDecoder();
            let buffer = '';

            try {
                while (true) {
                    const {done, value} = await reader.read();
                    
                    if (done) {
                        abortControllerRef.current = null;
                        break;
                    }
                    
                    const chunk = decoder.decode(value, {stream: true});
                    buffer += chunk;
                    
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';
                    
                    for (const line of lines) {
                        if (!line.trim() || !line.startsWith('data: ')) {
                            continue;
                        }

                        let data: {type?: string; message?: string; error?: string; content?: string; data?: Record<string, unknown>};
                        try {
                            data = JSON.parse(line.substring(6));
                        } catch (parseError) {
                            console.error('SSE parse error:', parseError, 'Line:', line);
                            continue;
                        }

                        if (data.type === 'status') {
                            setStreamingMessage(data.message || '');
                        } else if (data.type === 'content_chunk') {
                            const newContent = data.content || '';
                            if (newContent !== streamingContentRef.current) {
                                streamingContentRef.current = newContent;
                                setExplanation(newContent);
                                setStreamingMessage('');
                            }
                        } else if (data.type === 'result') {
                            const resultData = data.data as {result?: string; content?: string; explanation?: string; error?: string} | undefined;
                            if (resultData?.result) {
                                setExplanation(resultData.result);
                                setStreamingMessage('');
                            } else if (resultData?.content) {
                                setExplanation(resultData.content);
                                setStreamingMessage('');
                            } else if (resultData?.explanation) {
                                setExplanation(resultData.explanation);
                                setStreamingMessage('');
                            } else {
                                setError(resultData?.error || 'Failed to explain chart');
                                setStreamingMessage('');
                                break;
                            }
                        } else if (data.type === 'complete') {
                            setStreamingMessage('');
                            break;
                        } else if (data.type === 'error') {
                            setError(data.error || data.message || 'Streaming error occurred');
                            setStreamingMessage('');
                            break;
                        }
                    }
                }
            } finally {
                reader.releaseLock();
            }

        } catch (err: any) {
            abortControllerRef.current = null;
            
            if (err.name === 'AbortError') {
                setStreamingMessage('');
                return;
            }
            
            const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
            setError(errorMessage);
            console.error('Error explaining chart:', err);
        } finally {
            isExplainingRef.current = false;
            setIsExplaining(false);
        }
    }, [urls, userData, urlsLoaded]);

    const clearExplanation = useCallback(() => {
        setExplanation(null);
        setError(null);
        setStreamingMessage('');
        streamingContentRef.current = '';
    }, []);

    const cancelExplanation = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        isExplainingRef.current = false;
        setIsExplaining(false);
        setStreamingMessage('');
    }, []);

    return {
        isExplaining,
        explanation,
        error,
        streamingMessage,
        explainChart,
        clearExplanation,
        cancelExplanation
    };
};
